import {OfferRuleAgeBucket, OfferRuleFamily, OfferRuleModel} from '../../../../../model/offer-rule.model';
import {OfferRuleTypeEnumerator} from '../enumerators/offer-rule-type.enumerator';
import {EnhancedRuleDraft, EnhancedRuleServerMetadata} from '../models/enhanced-rule-draft.model';
import {
  GeneratedAprOfferDraft,
  GeneratedFinanceOfferDraft,
  GeneratedLeaseOfferDraft,
  GeneratedOfferSetDraft,
  MultiVinVisibilityScope
} from '../models/generated-offer-set-draft.model';
import {
  offerRuleWorkflowSchemaVersion,
  OfferRuleWorkflowAcceptance,
  OfferRuleWorkflowAggregateCollection,
  OfferRuleWorkflowAggregateItem,
  OfferRuleWorkflowAgeBucketData,
  OfferRuleWorkflowAgedInventoryRuleData,
  OfferRuleWorkflowAgedInventoryWriteData,
  OfferRuleWorkflowCommandStatusResponse,
  OfferRuleWorkflowCreateOperation,
  OfferRuleWorkflowDeleteOperation,
  OfferRuleWorkflowFinanceData,
  OfferRuleWorkflowLeaseData,
  OfferRuleWorkflowOffersData,
  OfferRuleWorkflowPromotionalAprData,
  OfferRuleWorkflowPrivacy,
  OfferRuleWorkflowRuleData,
  OfferRuleWorkflowUpdateOperation,
  OfferRuleWorkflowVisibility
} from '../models/offer-rule-workflow-api.model';
import {
  QualifyingRuleSourceAprOffer,
  QualifyingRuleSourceDetail,
  QualifyingRuleSourceFinanceOffer,
  QualifyingRuleSourceLeaseOffer
} from '../models/qualifying-rule-source-detail.model';
import {QualifyingRuleSourceRow} from '../models/qualifying-rule-source.model';

const defaultIsCustom = false;
const defaultUseDefaultSettings = true;
const decimalPattern = /^(0|[1-9]\d*)\.\d{2}$/;
const requestIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const maximumCurrency = '99999999.99';
const maximumPercentage = '999.99';
const maximumTextLength = 255;
const offerSetType = 'offer_set';
const dollarDiscountType = 'dollar_discount';
const percentageDiscountType = 'percentage_discount';
const agedInventoryType = 'aged_inventory';
const maximumDiscountPercentage = '100.00';
const maximumAgeDays = 2147483647;

type EnhancedRuleDraftNormalizer = (draft: EnhancedRuleDraft) => EnhancedRuleDraft;

export function mapSourceToGeneratedOfferSetDraft(source: QualifyingRuleSourceDetail): GeneratedOfferSetDraft
{
  const generatedOfferSet = {
    multiVinVisibility: MultiVinVisibilityScope.FullVisibility,
    lease: mapLeaseOffer(source.offers.lease),
    alternateLease: mapLeaseOffer(source.offers.alternateLease),
    finance: mapFinanceOffer(source.offers.finance),
    promotionalApr: mapAprOffer(source.offers.promotionalApr)
  };

  return generatedOfferSet;
}

export function cloneOfferRule(rule: OfferRuleModel): OfferRuleModel
{
  const clone: OfferRuleModel = {
    id: rule.id,
    accountId: rule.accountId,
    offerSetId: rule.offerSetId,
    year: rule.year,
    make: rule.make,
    model: rule.model,
    trim: rule.trim,
    minSalePrice: rule.minSalePrice,
    maxSalePrice: rule.maxSalePrice,
    vehicleLimit: rule.vehicleLimit,
    vehicleCondition: rule.vehicleCondition,
    dollarAmountDiscount: rule.dollarAmountDiscount,
    percentageDiscount: rule.percentageDiscount,
    discountBasis: rule.discountBasis ?? 'selling_price',
    offerRuleType: rule.offerRuleType,
    maxDiscountAmount: rule.maxDiscountAmount,
    ageBuckets: rule.ageBuckets?.map(bucket => ({...bucket})),
    isPrivate: rule.isPrivate,
    status: rule.status,
    created: rule.created,
    modified: rule.modified
  };

  return clone;
}

export function cloneQualifyingRuleSource(source: QualifyingRuleSourceRow): QualifyingRuleSourceRow
{
  const clone = {...source};

  return clone;
}

export function cloneGeneratedOfferSetDraft(generatedOfferSet: GeneratedOfferSetDraft): GeneratedOfferSetDraft
{
  const clone = {
    multiVinVisibility: generatedOfferSet.multiVinVisibility ?? MultiVinVisibilityScope.FullVisibility,
    lease: cloneLeaseOfferDraft(generatedOfferSet.lease),
    alternateLease: cloneLeaseOfferDraft(generatedOfferSet.alternateLease),
    finance: cloneFinanceOfferDraft(generatedOfferSet.finance),
    promotionalApr: cloneAprOfferDraft(generatedOfferSet.promotionalApr)
  };

  return clone;
}

export function cloneEnhancedRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const clone = {
    ruleType: draft.ruleType,
    rule: cloneOfferRule(draft.rule),
    source: cloneOptionalQualifyingRuleSource(draft.source),
    generatedOfferSet: cloneOptionalGeneratedOfferSetDraft(draft.generatedOfferSet),
    serverMetadata: cloneOptionalServerMetadata(draft.serverMetadata)
  };

  return clone;
}

function normalizeOfferSetRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const normalizedDraft = cloneEnhancedRuleDraft(draft);
  normalizedDraft.rule.offerRuleType = 'offerSet';

  return normalizedDraft;
}

export function normalizeDollarDiscountRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const normalizedDraft = cloneEnhancedRuleDraft(draft);
  normalizedDraft.ruleType = OfferRuleTypeEnumerator.DollarDiscountRule;
  normalizedDraft.rule.offerRuleType = 'dollarDiscount';
  normalizedDraft.rule.isPrivate = true;
  delete normalizedDraft.rule.offerSetId;
  normalizedDraft.source = null;
  normalizedDraft.generatedOfferSet = null;

  return normalizedDraft;
}

/** Keeps aged drafts source-free while retaining saved ranges, values, cap, and shared basis. */
export function normalizeAgedInventoryRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const normalizedDraft = cloneEnhancedRuleDraft(draft);
  normalizedDraft.ruleType = OfferRuleTypeEnumerator.AgedInventoryRule;
  normalizedDraft.rule.offerRuleType = 'agedInventory';
  normalizedDraft.rule.isPrivate = true;
  delete normalizedDraft.rule.offerSetId;
  delete normalizedDraft.rule.dollarAmountDiscount;
  delete normalizedDraft.rule.percentageDiscount;
  normalizedDraft.source = null;
  normalizedDraft.generatedOfferSet = null;

  return normalizedDraft;
}

/** Explicit family metadata wins; older models retain their existing association-based fallback. */
export function resolveOfferRuleFamily(rule: OfferRuleModel): OfferRuleFamily
{
  let family = rule.offerRuleType ?? 'dollarDiscount';
  if (rule.offerRuleType == null && rule.offerSetId != null) {
    family = 'offerSet';
  }

  return family;
}

const enhancedRuleDraftNormalizers: Record<OfferRuleTypeEnumerator, EnhancedRuleDraftNormalizer> = {
  [OfferRuleTypeEnumerator.OfferSetRule]: normalizeOfferSetRuleDraft,
  [OfferRuleTypeEnumerator.DollarDiscountRule]: normalizeDollarDiscountRuleDraft,
  [OfferRuleTypeEnumerator.AgedInventoryRule]: normalizeAgedInventoryRuleDraft
};

export function normalizeEnhancedRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const normalizer = enhancedRuleDraftNormalizers[draft.ruleType];
  const normalizedDraft = normalizer(draft);

  return normalizedDraft;
}

export function selectOfferRuleForPersistence(draft: EnhancedRuleDraft): OfferRuleModel
{
  const normalizedDraft = normalizeEnhancedRuleDraft(draft);
  const rule = cloneOfferRule(normalizedDraft.rule);
  if (normalizedDraft.rule.offerSetId === undefined) {
    delete rule.offerSetId;
  }

  return rule;
}

function mapLeaseOffer(source: QualifyingRuleSourceLeaseOffer | null): GeneratedLeaseOfferDraft
{
  const draft = createBlankLeaseOfferDraft();
  if (source !== null) {
    draft.paymentAmount = normalizeOptionalSourceDecimal(source.paymentAmount);
    draft.moneyDown = normalizeOptionalSourceDecimal(source.moneyDown);
    draft.percentageDown = normalizeOptionalSourceDecimal(source.percentageDown);
    draft.term = source.term;
    draft.annualMileage = source.annualMileage;
    draft.additionalText = source.additionalText;
  }

  return draft;
}

function createBlankLeaseOfferDraft(): GeneratedLeaseOfferDraft
{
  const draft = {
    paymentAmount: null,
    moneyDown: null,
    percentageDown: null,
    term: null,
    annualMileage: null,
    additionalText: null,
    isCustom: defaultIsCustom
  };

  return draft;
}

function mapFinanceOffer(source: QualifyingRuleSourceFinanceOffer | null): GeneratedFinanceOfferDraft
{
  const draft = createBlankFinanceOfferDraft();
  if (source !== null) {
    draft.paymentAmount = normalizeOptionalSourceDecimal(source.paymentAmount);
    draft.moneyDown = normalizeOptionalSourceDecimal(source.moneyDown);
    draft.percentageDown = normalizeOptionalSourceDecimal(source.percentageDown);
    draft.apr = normalizeOptionalSourceDecimal(source.apr);
    draft.term = source.term;
    draft.additionalText = source.additionalText;
  }

  return draft;
}

function createBlankFinanceOfferDraft(): GeneratedFinanceOfferDraft
{
  const draft = {
    paymentAmount: null,
    moneyDown: null,
    percentageDown: null,
    apr: null,
    term: null,
    additionalText: null,
    isCustom: defaultIsCustom
  };

  return draft;
}

function mapAprOffer(source: QualifyingRuleSourceAprOffer | null): GeneratedAprOfferDraft
{
  const draft = createBlankAprOfferDraft();
  if (source !== null) {
    draft.apr = normalizeOptionalSourceDecimal(source.apr);
    draft.term = source.term;
    draft.additionalText = source.additionalText;
  }

  return draft;
}

function createBlankAprOfferDraft(): GeneratedAprOfferDraft
{
  const draft = {
    apr: null,
    term: null,
    additionalText: null,
    useDefaultSettings: defaultUseDefaultSettings
  };

  return draft;
}

function cloneLeaseOfferDraft(draft: GeneratedLeaseOfferDraft): GeneratedLeaseOfferDraft
{
  const clone = {...draft};

  return clone;
}

function cloneFinanceOfferDraft(draft: GeneratedFinanceOfferDraft): GeneratedFinanceOfferDraft
{
  const clone = {...draft};

  return clone;
}

function cloneAprOfferDraft(draft: GeneratedAprOfferDraft): GeneratedAprOfferDraft
{
  const clone = {...draft};

  return clone;
}

function cloneOptionalQualifyingRuleSource(source: QualifyingRuleSourceRow | null): QualifyingRuleSourceRow | null
{
  let clone: QualifyingRuleSourceRow | null = null;
  if (source !== null) {
    clone = cloneQualifyingRuleSource(source);
  }

  return clone;
}

function cloneOptionalGeneratedOfferSetDraft(generatedOfferSet: GeneratedOfferSetDraft | null): GeneratedOfferSetDraft | null
{
  let clone: GeneratedOfferSetDraft | null = null;
  if (generatedOfferSet !== null) {
    clone = cloneGeneratedOfferSetDraft(generatedOfferSet);
  }

  return clone;
}

function cloneOptionalServerMetadata(metadata: EnhancedRuleServerMetadata | null): EnhancedRuleServerMetadata | null
{
  let clone: EnhancedRuleServerMetadata | null = null;
  if (metadata !== null) {
    clone = {
      ruleId: metadata.ruleId,
      version: metadata.version,
      generatedOfferSetId: metadata.generatedOfferSetId
    };
  }

  return clone;
}

function normalizeOptionalSourceDecimal(value: number | null): string | null
{
  let normalizedValue: string | null = null;
  if (value !== null && Number.isFinite(value) && value >= 0) {
    normalizedValue = value.toFixed(2);
  }

  return normalizedValue;
}

export function mapAggregateItemToEnhancedRuleDraft(
  item: OfferRuleWorkflowAggregateItem,
  accountId: number
): EnhancedRuleDraft
{
  const rule = mapAggregateRule(item, accountId);
  let generatedOfferSet: GeneratedOfferSetDraft | null = null;
  let generatedOfferSetId: number | null = null;
  let ruleType = OfferRuleTypeEnumerator.DollarDiscountRule;
  if (item.type === offerSetType) {
    ruleType = OfferRuleTypeEnumerator.OfferSetRule;
    generatedOfferSet = mapPersistedOfferSet(item.offerSet.offers, item.offerSet.visibilityScope);
    generatedOfferSetId = item.offerSet.generatedOfferSetId;
  }
  if (item.type === agedInventoryType) {
    ruleType = OfferRuleTypeEnumerator.AgedInventoryRule;
  }
  const draft: EnhancedRuleDraft = {
    ruleType,
    rule,
    source: null,
    generatedOfferSet,
    serverMetadata: {ruleId: item.ruleId, version: item.version, generatedOfferSetId}
  };

  return draft;
}

export function mapDraftToCreateOperation(
  clientKey: string,
  draft: EnhancedRuleDraft
): OfferRuleWorkflowCreateOperation
{
  const builders: Record<OfferRuleTypeEnumerator, (key: string, value: EnhancedRuleDraft) => OfferRuleWorkflowCreateOperation> = {
    [OfferRuleTypeEnumerator.OfferSetRule]: mapOfferSetCreateOperation,
    [OfferRuleTypeEnumerator.DollarDiscountRule]: mapDiscountCreateOperation,
    [OfferRuleTypeEnumerator.AgedInventoryRule]: mapAgedCreateOperation
  };
  const builder = builders[draft.ruleType];
  const operation = builder(clientKey, draft);

  return operation;
}

export function mapDraftToUpdateOperation(draft: EnhancedRuleDraft): OfferRuleWorkflowUpdateOperation
{
  const builders: Record<OfferRuleTypeEnumerator, (value: EnhancedRuleDraft) => OfferRuleWorkflowUpdateOperation> = {
    [OfferRuleTypeEnumerator.OfferSetRule]: mapOfferSetUpdateOperation,
    [OfferRuleTypeEnumerator.DollarDiscountRule]: mapDiscountUpdateOperation,
    [OfferRuleTypeEnumerator.AgedInventoryRule]: mapAgedUpdateOperation
  };
  const builder = builders[draft.ruleType];
  const operation = builder(draft);

  return operation;
}

/** Preserves source selection and generated offers for ordinary Offer Set creation. */
function mapOfferSetCreateOperation(clientKey: string, draft: EnhancedRuleDraft): OfferRuleWorkflowCreateOperation
{
  const rule = mapDraftRuleData(draft);
  const sourceOfferSetId = requirePositiveSafeInteger(draft.source?.offerSetId, 'sourceOfferSetId');
  const generated = requireGeneratedDraft(draft);
  const operation: OfferRuleWorkflowCreateOperation = {
    clientKey,
    type: offerSetType,
    rule,
    offerSet: {
      sourceOfferSetId,
      visibilityScope: generated.multiVinVisibility,
      offers: mapGeneratedOffers(generated)
    }
  };

  return operation;
}

/** Retains the existing scalar discount subtype and request fields. */
function mapDiscountCreateOperation(clientKey: string, draft: EnhancedRuleDraft): OfferRuleWorkflowCreateOperation
{
  const rule = mapDraftRuleData(draft);
  const operation: OfferRuleWorkflowCreateOperation = {clientKey, type: resolveDiscountType(draft.rule), rule, offerSet: null};

  return operation;
}

/** Aged creation carries the complete strategy without a generated Offer Set. */
function mapAgedCreateOperation(clientKey: string, draft: EnhancedRuleDraft): OfferRuleWorkflowCreateOperation
{
  const rule = mapDraftAgedRuleData(draft);
  const operation: OfferRuleWorkflowCreateOperation = {clientKey, type: agedInventoryType, rule, offerSet: null};

  return operation;
}

/** Keeps generated Offer Set edits within the existing optimistic version contract. */
function mapOfferSetUpdateOperation(draft: EnhancedRuleDraft): OfferRuleWorkflowUpdateOperation
{
  const metadata = requireServerMetadata(draft);
  const rule = mapDraftRuleData(draft);
  const generated = requireGeneratedDraft(draft);
  const operation: OfferRuleWorkflowUpdateOperation = {
    ruleId: metadata.ruleId,
    version: metadata.version,
    type: offerSetType,
    rule,
    offerSet: {
      visibilityScope: generated.multiVinVisibility,
      offers: mapGeneratedOffers(generated)
    }
  };

  return operation;
}

/** Retains scalar conversion within the ordinary Discount family. */
function mapDiscountUpdateOperation(draft: EnhancedRuleDraft): OfferRuleWorkflowUpdateOperation
{
  const metadata = requireServerMetadata(draft);
  const rule = mapDraftRuleData(draft);
  const operation: OfferRuleWorkflowUpdateOperation = {
    ruleId: metadata.ruleId,
    version: metadata.version,
    type: resolveDiscountType(draft.rule),
    rule,
    offerSet: null
  };

  return operation;
}

/** Replaces the complete aged strategy using its last authoritative version. */
function mapAgedUpdateOperation(draft: EnhancedRuleDraft): OfferRuleWorkflowUpdateOperation
{
  const metadata = requireServerMetadata(draft);
  const rule = mapDraftAgedRuleData(draft);
  const operation: OfferRuleWorkflowUpdateOperation = {
    ruleId: metadata.ruleId,
    version: metadata.version,
    type: agedInventoryType,
    rule,
    offerSet: null
  };

  return operation;
}

export function mapDraftToDeleteOperation(draft: EnhancedRuleDraft): OfferRuleWorkflowDeleteOperation
{
  const metadata = requireServerMetadata(draft);
  const operation = {ruleId: metadata.ruleId, version: metadata.version};

  return operation;
}

export function areEnhancedRuleDraftsEqual(first: EnhancedRuleDraft, second: EnhancedRuleDraft): boolean
{
  const firstValue = buildComparableDraft(first);
  const secondValue = buildComparableDraft(second);
  const equal = JSON.stringify(firstValue) === JSON.stringify(secondValue);

  return equal;
}

function mapAggregateRule(item: OfferRuleWorkflowAggregateItem, accountId: number): OfferRuleModel
{
  const rule = new OfferRuleModel();
  rule.id = item.ruleId;
  rule.accountId = accountId;
  rule.offerRuleType = 'dollarDiscount';
  if (item.type === offerSetType) {
    rule.offerSetId = item.offerSet.generatedOfferSetId;
    rule.offerRuleType = 'offerSet';
  }
  rule.year = item.rule.year ?? undefined;
  rule.make = item.rule.make ?? undefined;
  rule.model = item.rule.model ?? undefined;
  rule.trim = item.rule.trim ?? undefined;
  if (item.rule.minSalePrice !== null && item.rule.minSalePrice !== undefined) {
    rule.minSalePrice = Number(item.rule.minSalePrice);
  }
  rule.maxSalePrice = Number(item.rule.maxSalePrice);
  rule.vehicleLimit = item.rule.vehicleLimit ?? undefined;
  rule.vehicleCondition = item.rule.vehicleCondition;
  applyAggregateDiscount(rule, item);
  rule.status = item.rule.status;
  rule.isPrivate = item.rule.privacy === 'private';
  rule.created = item.created ?? undefined;
  rule.modified = item.modified ?? undefined;

  return rule;
}

/** Maps each persisted discount family explicitly without treating aged rules as scalar discounts. */
function applyAggregateDiscount(rule: OfferRuleModel, item: OfferRuleWorkflowAggregateItem): void
{
  rule.dollarAmountDiscount = undefined;
  if (item.type === dollarDiscountType && item.rule.dollarAmountDiscount != null) {
    rule.dollarAmountDiscount = Number(item.rule.dollarAmountDiscount);
  }
  if (item.type === percentageDiscountType && item.rule.percentageDiscount != null) {
    rule.percentageDiscount = Number(item.rule.percentageDiscount);
    rule.discountBasis = item.rule.discountBasis ?? 'selling_price';
  }
  if (item.type === agedInventoryType) {
    rule.offerRuleType = 'agedInventory';
    rule.discountBasis = item.rule.discountBasis;
    rule.maxDiscountAmount = null;
    if (item.rule.maxDiscountAmount !== null) {
      rule.maxDiscountAmount = Number(item.rule.maxDiscountAmount);
    }
    rule.ageBuckets = item.rule.ageBuckets.map(mapPersistedAgeBucket);
  }
}

/** Copies each saved row so later edits cannot mutate authoritative response objects. */
function mapPersistedAgeBucket(bucket: OfferRuleWorkflowAgeBucketData): OfferRuleAgeBucket
{
  const mapped = {...bucket, discountValue: Number(bucket.discountValue)};

  return mapped;
}

function mapPersistedOfferSet(
  offers: OfferRuleWorkflowOffersData,
  visibility: OfferRuleWorkflowVisibility
): GeneratedOfferSetDraft
{
  let multiVinVisibility = MultiVinVisibilityScope.FullVisibility;
  if (visibility === MultiVinVisibilityScope.InventoryOnly) {
    multiVinVisibility = MultiVinVisibilityScope.InventoryOnly;
  }
  const generated: GeneratedOfferSetDraft = {
    multiVinVisibility,
    lease: mapPersistedLease(offers.lease),
    alternateLease: mapPersistedLease(offers.alternateLease),
    finance: mapPersistedFinance(offers.finance),
    promotionalApr: mapPersistedApr(offers.promotionalApr)
  };

  return generated;
}

function mapPersistedLease(lease: OfferRuleWorkflowLeaseData): GeneratedLeaseOfferDraft
{
  const draft: GeneratedLeaseOfferDraft = {
    paymentAmount: lease.paymentAmount,
    moneyDown: lease.moneyDown,
    percentageDown: lease.percentageDown,
    term: lease.term,
    annualMileage: lease.annualMileage,
    additionalText: lease.additionalText,
    isCustom: lease.mode === 'custom'
  };

  return draft;
}

function mapPersistedFinance(finance: OfferRuleWorkflowFinanceData): GeneratedFinanceOfferDraft
{
  const draft: GeneratedFinanceOfferDraft = {
    paymentAmount: finance.paymentAmount,
    moneyDown: finance.moneyDown,
    percentageDown: finance.percentageDown,
    apr: finance.apr,
    term: finance.term,
    additionalText: finance.additionalText,
    isCustom: finance.mode === 'custom'
  };

  return draft;
}

function mapPersistedApr(apr: OfferRuleWorkflowPromotionalAprData): GeneratedAprOfferDraft
{
  const draft: GeneratedAprOfferDraft = {
    apr: apr.apr,
    term: apr.term,
    additionalText: apr.additionalText,
    useDefaultSettings: apr.mode === 'default'
  };

  return draft;
}

/** Selects the API subtype while retaining the existing discount-rule UI family. */
function resolveDiscountType(rule: OfferRuleModel): 'dollar_discount' | 'percentage_discount'
{
  let type: 'dollar_discount' | 'percentage_discount' = dollarDiscountType;
  if (rule.percentageDiscount != null) {
    type = percentageDiscountType;
  }

  return type;
}

/** Accepts exactly one discount value, including a valid zero-percent rule. */
export function isValidDiscountRuleValue(rule: OfferRuleModel): boolean
{
  const percentage = rule.percentageDiscount;
  const dollar = rule.dollarAmountDiscount;
  let valid = dollar != null && Number.isFinite(dollar) && dollar > 0 && percentage == null;
  if (percentage != null) {
    valid = dollar == null && Number.isFinite(percentage) && percentage >= 0
      && percentage <= Number(maximumDiscountPercentage) && /^\d+(?:\.\d{1,2})?$/.test(String(percentage));
  }

  return valid;
}

/** Serializes only the active subtype using the workflow's two-decimal contract. */
function mapDraftDiscountValues(
  draft: EnhancedRuleDraft
): Pick<OfferRuleWorkflowRuleData, 'dollarAmountDiscount' | 'percentageDiscount' | 'discountBasis'>
{
  let values: Pick<OfferRuleWorkflowRuleData, 'dollarAmountDiscount' | 'percentageDiscount' | 'discountBasis'> = {};
  if (draft.ruleType === OfferRuleTypeEnumerator.DollarDiscountRule && draft.rule.percentageDiscount == null) {
    values = {dollarAmountDiscount: normalizeRequiredDecimal(draft.rule.dollarAmountDiscount, maximumCurrency, true)};
  }
  if (draft.ruleType === OfferRuleTypeEnumerator.DollarDiscountRule && draft.rule.percentageDiscount != null) {
    values = {
      percentageDiscount: normalizeRequiredDecimal(draft.rule.percentageDiscount, maximumDiscountPercentage, false),
      discountBasis: draft.rule.discountBasis ?? 'selling_price'
    };
  }

  return values;
}

function mapDraftRuleData(draft: EnhancedRuleDraft): OfferRuleWorkflowRuleData
{
  assertOrdinaryDraftHasNoAgedConfiguration(draft);
  const condition = normalizeVehicleCondition(draft.rule.vehicleCondition);
  const status = normalizeRuleStatus(draft.rule.status);
  let privacy: OfferRuleWorkflowPrivacy = 'public';
  if (draft.rule.isPrivate) {
    privacy = 'private';
  }
  const rule: OfferRuleWorkflowRuleData = {
    year: normalizeOptionalSafeInteger(draft.rule.year, true, 'year'),
    make: normalizeOptionalText(draft.rule.make),
    model: normalizeOptionalText(draft.rule.model),
    trim: normalizeOptionalText(draft.rule.trim),
    minSalePrice: normalizeOptionalDecimal(draft.rule.minSalePrice, maximumCurrency),
    maxSalePrice: normalizeRequiredDecimal(draft.rule.maxSalePrice, maximumCurrency, false),
    vehicleLimit: normalizeOptionalSafeInteger(draft.rule.vehicleLimit, false, 'vehicleLimit'),
    vehicleCondition: condition,
    ...mapDraftDiscountValues(draft),
    status,
    privacy
  };

  if (!isValidMinimumPrice(rule.minSalePrice, rule.maxSalePrice)) {
    throw new RangeError('Minimum Price must be less than or equal to Maximum Price.');
  }

  return rule;
}

/** Rejects meaningful aged data on ordinary drafts instead of silently discarding it during serialization. */
function assertOrdinaryDraftHasNoAgedConfiguration(draft: EnhancedRuleDraft): void
{
  if (draft.ruleType !== OfferRuleTypeEnumerator.AgedInventoryRule
    && (draft.rule.maxDiscountAmount != null || (draft.rule.ageBuckets?.length ?? 0) > 0)) {
    throw new RangeError('Aged configuration requires an Aged Inventory Rule.');
  }
}

/** Aged writes carry complete configuration and omit inactive scalar-discount fields entirely. */
function mapDraftAgedRuleData(draft: EnhancedRuleDraft): OfferRuleWorkflowAgedInventoryWriteData
{
  if (!isValidAgedInventoryRuleValue(draft.rule) || draft.source !== null || draft.generatedOfferSet !== null) {
    throw new RangeError('The complete aged inventory configuration is invalid.');
  }
  const rule = normalizeDraftAgedRuleData(draft);

  return rule;
}

/** Preserves historical zero caps for comparison without applying write-only validation. */
function normalizeDraftAgedRuleData(draft: EnhancedRuleDraft): OfferRuleWorkflowAgedInventoryWriteData
{
  const rule: OfferRuleWorkflowAgedInventoryWriteData = {
    ...mapDraftRuleData(draft),
    privacy: 'private',
    discountBasis: draft.rule.discountBasis ?? 'selling_price',
    maxDiscountAmount: normalizeOptionalDecimal(draft.rule.maxDiscountAmount, maximumCurrency),
    ageBuckets: draft.rule.ageBuckets.map(mapDraftAgeBucket)
  };

  return rule;
}

/** Keeps dollar and percentage units unchanged while normalizing transport precision. */
function mapDraftAgeBucket(bucket: OfferRuleAgeBucket): OfferRuleWorkflowAgeBucketData
{
  let maximum = maximumCurrency;
  if (bucket.discountType === 'percentage') {
    maximum = maximumDiscountPercentage;
  }
  const mapped: OfferRuleWorkflowAgeBucketData = {
    minAgeDays: bucket.minAgeDays,
    maxAgeDays: bucket.maxAgeDays,
    discountType: bucket.discountType,
    discountValue: normalizeRequiredDecimal(bucket.discountValue, maximum, false)
  };

  return mapped;
}

/** Zero-cap compatibility is only for reopening existing drafts; staging and writes require a positive cap. */
export function isValidAgedInventoryRuleValue(rule: OfferRuleModel, allowZeroCap = false): boolean
{
  const buckets = rule.ageBuckets;
  const basis = rule.discountBasis ?? 'selling_price';
  const valid = rule.isPrivate === true && rule.offerSetId == null
    && rule.dollarAmountDiscount == null && rule.percentageDiscount == null
    && (basis === 'selling_price' || basis === 'msrp')
    && isDraftDecimal(rule.maxSalePrice, maximumCurrency)
    && (rule.maxDiscountAmount == null || (isDraftDecimal(rule.maxDiscountAmount, maximumCurrency)
      && (allowZeroCap || rule.maxDiscountAmount > 0)))
    && Array.isArray(buckets) && buckets.length > 0
    && buckets.every(bucket => isValidDraftAgeBucket(bucket, rule.maxDiscountAmount))
    && hasContinuousAgeRanges(buckets);

  return valid;
}

/** Numeric draft values must fit the database scale before conversion to exact decimal strings. */
function isDraftDecimal(value: unknown, maximum: string): value is number
{
  const valid = typeof value === 'number' && Number.isFinite(value) && value >= 0
    && value <= Number(maximum) && /^(0|[1-9]\d*)(\.\d{1,2})?$/.test(String(value));

  return valid;
}

/** Only dollar values are compared to the dollar cap; percentage caps apply later in processing. */
function isValidDraftAgeBucket(bucket: unknown, cap: number | null | undefined): bucket is OfferRuleAgeBucket
{
  let valid = false;
  if (isRecord(bucket)) {
    const validRange = isAgeDay(bucket.minAgeDays)
      && (bucket.maxAgeDays === null || (isAgeDay(bucket.maxAgeDays) && bucket.maxAgeDays >= bucket.minAgeDays));
    let validValue = bucket.discountType === 'dollar'
      && isDraftDecimal(bucket.discountValue, maximumCurrency)
      && (cap == null || bucket.discountValue <= cap);
    if (bucket.discountType === 'percentage') {
      validValue = isDraftDecimal(bucket.discountValue, maximumDiscountPercentage);
    }
    valid = validRange && validValue;
  }

  return valid;
}

/** Mirrors the signed SQL integer range used for inclusive inventory-age bounds. */
function isAgeDay(value: unknown): value is number
{
  const valid = typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= maximumAgeDays;

  return valid;
}

/** A finite final maximum is allowed; an open-ended range cannot have a following bucket. */
function hasContinuousAgeRanges(buckets: readonly {minAgeDays: number; maxAgeDays: number | null}[]): boolean
{
  let expectedMinimum: number | null = 0;
  let valid = true;
  for (const bucket of buckets) {
    if (bucket.minAgeDays !== expectedMinimum) {
      valid = false;
    }
    expectedMinimum = null;
    if (bucket.maxAgeDays !== null) {
      expectedMinimum = bucket.maxAgeDays + 1;
    }
  }

  return valid;
}

function mapGeneratedOffers(generated: GeneratedOfferSetDraft): OfferRuleWorkflowOffersData
{
  const offers: OfferRuleWorkflowOffersData = {
    lease: mapGeneratedLease(generated.lease),
    alternateLease: mapGeneratedLease(generated.alternateLease),
    finance: mapGeneratedFinance(generated.finance),
    promotionalApr: mapGeneratedApr(generated.promotionalApr)
  };

  return offers;
}

function mapGeneratedLease(draft: GeneratedLeaseOfferDraft): OfferRuleWorkflowLeaseData
{
  let mode: 'custom' | 'default' = 'default';
  if (draft.isCustom) {
    mode = 'custom';
  }
  const lease: OfferRuleWorkflowLeaseData = {
    mode,
    paymentAmount: normalizeOptionalDecimal(draft.paymentAmount, maximumCurrency),
    moneyDown: normalizeOptionalDecimal(draft.moneyDown, maximumCurrency),
    percentageDown: normalizeOptionalDecimal(draft.percentageDown, maximumPercentage),
    term: normalizeOptionalSafeInteger(draft.term, true, 'term'),
    annualMileage: normalizeOptionalSafeInteger(draft.annualMileage, false, 'annualMileage'),
    additionalText: normalizeOptionalText(draft.additionalText)
  };
  validateCustomLease(lease);

  return lease;
}

function mapGeneratedFinance(draft: GeneratedFinanceOfferDraft): OfferRuleWorkflowFinanceData
{
  let mode: 'custom' | 'default' = 'default';
  if (draft.isCustom) {
    mode = 'custom';
  }
  const finance: OfferRuleWorkflowFinanceData = {
    mode,
    paymentAmount: normalizeOptionalDecimal(draft.paymentAmount, maximumCurrency),
    moneyDown: normalizeOptionalDecimal(draft.moneyDown, maximumCurrency),
    percentageDown: normalizeOptionalDecimal(draft.percentageDown, maximumPercentage),
    apr: normalizeOptionalDecimal(draft.apr, maximumPercentage),
    term: normalizeOptionalSafeInteger(draft.term, true, 'term'),
    additionalText: normalizeOptionalText(draft.additionalText)
  };
  validateCustomFinance(finance);

  return finance;
}

function mapGeneratedApr(draft: GeneratedAprOfferDraft): OfferRuleWorkflowPromotionalAprData
{
  let mode: 'custom' | 'default' = 'custom';
  if (draft.useDefaultSettings) {
    mode = 'default';
  }
  const apr: OfferRuleWorkflowPromotionalAprData = {
    mode,
    apr: normalizeOptionalDecimal(draft.apr, maximumPercentage),
    term: normalizeOptionalSafeInteger(draft.term, true, 'term'),
    additionalText: normalizeOptionalText(draft.additionalText)
  };
  if (mode === 'custom' && (apr.apr === null || apr.term === null)) {
    throw new RangeError('Custom Promotional APR values are incomplete.');
  }

  return apr;
}

function validateCustomLease(lease: OfferRuleWorkflowLeaseData): void
{
  const activeDownValues = Number(lease.moneyDown !== null) + Number(lease.percentageDown !== null);
  if (lease.mode === 'custom'
    && (lease.paymentAmount === null || lease.term === null || lease.annualMileage === null || activeDownValues !== 1)
  ) {
    throw new RangeError('Custom lease values are incomplete or contradictory.');
  }
}

function validateCustomFinance(finance: OfferRuleWorkflowFinanceData): void
{
  const activeDownValues = Number(finance.moneyDown !== null) + Number(finance.percentageDown !== null);
  if (finance.mode === 'custom'
    && (finance.paymentAmount === null || finance.apr === null || finance.term === null || activeDownValues !== 1)
  ) {
    throw new RangeError('Custom finance values are incomplete or contradictory.');
  }
}

function requireGeneratedDraft(draft: EnhancedRuleDraft): GeneratedOfferSetDraft
{
  if (draft.generatedOfferSet === null) {
    throw new RangeError('The complete generated offer set is required.');
  }

  return draft.generatedOfferSet;
}

function requireServerMetadata(draft: EnhancedRuleDraft): EnhancedRuleServerMetadata
{
  const metadata = draft.serverMetadata;
  if (metadata === null || !Number.isSafeInteger(metadata.ruleId) || metadata.ruleId <= 0 || metadata.version.trim() === '') {
    throw new RangeError('Authoritative rule identity and version are required.');
  }

  return metadata;
}

function requirePositiveSafeInteger(value: unknown, name: string): number
{
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive safe integer.`);
  }

  return value;
}

function normalizeOptionalSafeInteger(
  value: unknown,
  positive: boolean,
  name: string
): number | null
{
  let normalized: number | null = null;
  if (value !== null && value !== undefined && value !== '') {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || (positive && value === 0)) {
      throw new RangeError(`${name} is invalid.`);
    }
    normalized = value;
  }

  return normalized;
}

function normalizeRequiredDecimal(value: unknown, maximum: string, positive: boolean): string
{
  const normalized = normalizeOptionalDecimal(value, maximum);
  if (normalized === null || (positive && normalized === '0.00')) {
    throw new RangeError('A required financial value is invalid.');
  }

  return normalized;
}

function normalizeOptionalDecimal(value: unknown, maximum: string): string | null
{
  let normalized: string | null = null;
  if (value !== null && value !== undefined && value !== '') {
    let text = String(value).trim();
    if (typeof value === 'number') {
      text = value.toString();
    }
    if (!/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(text)) {
      throw new RangeError('Financial values allow at most two decimal places.');
    }
    const parts = text.split('.');
    const decimalPart = (parts[1] ?? '').padEnd(2, '0');
    normalized = `${parts[0]}.${decimalPart}`;
    if (!decimalPattern.test(normalized) || compareDecimal(normalized, maximum) > 0) {
      throw new RangeError('A financial value exceeds the supported range.');
    }
  }

  return normalized;
}

function compareDecimal(left: string, right: string): number
{
  let comparison = left.length - right.length;
  if (comparison === 0) {
    comparison = left.localeCompare(right);
  }

  return comparison;
}

function normalizeOptionalText(value: unknown): string | null
{
  let normalized: string | null = null;
  if (typeof value === 'string' && value.trim() !== '') {
    if (value.length > maximumTextLength) {
      throw new RangeError('Text values may not exceed 255 characters.');
    }
    normalized = value;
  }

  return normalized;
}

function normalizeVehicleCondition(value: unknown): 'New' | 'Used' | 'CPO' | 'Any'
{
  const allowed = ['New', 'Used', 'CPO', 'Any'];
  if (typeof value !== 'string' || !allowed.includes(value)) {
    throw new RangeError('Vehicle condition is invalid.');
  }

  return value as 'New' | 'Used' | 'CPO' | 'Any';
}

function normalizeRuleStatus(value: unknown): 'Active' | 'Inactive'
{
  if (value !== 'Active' && value !== 'Inactive') {
    throw new RangeError('Rule status is invalid.');
  }

  return value;
}

/** Compares saved configuration without requiring historical values to satisfy current write rules. */
function buildComparableDraft(draft: EnhancedRuleDraft): object
{
  let generatedOfferSet: OfferRuleWorkflowOffersData | null = null;
  let rule = mapDraftRuleData(draft);
  if (draft.ruleType === OfferRuleTypeEnumerator.AgedInventoryRule) {
    rule = normalizeDraftAgedRuleData(draft);
  }
  if (draft.generatedOfferSet !== null) {
    generatedOfferSet = mapGeneratedOffers(draft.generatedOfferSet);
  }
  const comparable = {
    ruleType: draft.ruleType,
    rule,
    generatedOfferSet,
    visibility: draft.generatedOfferSet?.multiVinVisibility ?? null
  };

  return comparable;
}

export function parseOfferRuleWorkflowAggregateCollection(value: unknown): OfferRuleWorkflowAggregateCollection
{
  if (!isAggregateCollection(value)) {
    throw new TypeError('The aggregate response is invalid.');
  }

  return value;
}

export function parseOfferRuleWorkflowAcceptance(value: unknown): OfferRuleWorkflowAcceptance
{
  if (!isAcceptance(value)) {
    throw new TypeError('The workflow acceptance response is invalid.');
  }

  return value;
}

export function parseOfferRuleWorkflowCommandStatus(value: unknown): OfferRuleWorkflowCommandStatusResponse
{
  if (!isCommandStatus(value)) {
    throw new TypeError('The workflow command status response is invalid.');
  }

  return value;
}

function isAggregateCollection(value: unknown): value is OfferRuleWorkflowAggregateCollection
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['schemaVersion', 'accountId', 'items'])) {
    const items = value['items'];
    valid = value['schemaVersion'] === offerRuleWorkflowSchemaVersion
      && isPositiveSafeInteger(value['accountId'])
      && Array.isArray(items)
      && items.every((item) => isAggregateItem(item))
      && hasUniqueItemIds(items);
  }

  return valid;
}

/**
 ** Validates rule aggregates while accepting responses without optional metadata.
 */
function isAggregateItem(value: unknown): value is OfferRuleWorkflowAggregateItem
{
  let valid = false;
  const requiredKeys = ['ruleId', 'type', 'version', 'rule', 'offerSet'];
  const optionalKeys = ['created', 'modified', 'vehiclesImpacted', 'customOfferPreview'];
  const validShape = isRecord(value)
    && hasRequiredAndOptionalKeys(value, requiredKeys, optionalKeys);
  if (validShape && isRecord(value)) {
    const type = value['type'];
    const baseValid = isPositiveSafeInteger(value['ruleId'])
      && isOptionalNonNegativeSafeInteger(value['vehiclesImpacted'] ?? null)
      && isCustomOfferPreview(value['customOfferPreview'], type)
      && isNonEmptyString(value['version'])
      && isOptionalTimestamp(value['created'] ?? null)
      && isOptionalTimestamp(value['modified'] ?? null)
      && isRuleData(value['rule'], type);
    if (type === offerSetType) {
      valid = baseValid && isPersistedOfferSet(value['offerSet']);
    }
    if (type === dollarDiscountType || type === percentageDiscountType || type === agedInventoryType) {
      valid = baseValid && value['offerSet'] === null;
    }
  }

  return valid;
}

/**
 ** Validates preview sections while accepting older responses without previews.
 */
function isCustomOfferPreview(value: unknown, type: unknown): boolean
{
  let valid = value === undefined;
  if (Array.isArray(value)) {
    const labels = value.filter(isRecord).map(section => section['label']);
    valid = value.every(isPreviewSection)
      && new Set(labels).size === value.length
      && (type === offerSetType || value.length === 0);
  }

  return valid;
}

/**
 ** Checks preview fields against the offer type's display requirements.
 */
function isPreviewSection(value: unknown): boolean
{
  const keys = ['label', 'paymentAmount', 'term', 'upfrontAmount', 'upfrontLabel', 'apr'];
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    const label = value['label'];
    valid = ['Lease', 'Finance', 'Alt Lease', 'APR'].includes(String(label))
      && isPositiveSafeInteger(value['term'])
      && isPreviewAmount(value['paymentAmount'], label === 'APR')
      && isPreviewAmount(value['upfrontAmount'], label === 'APR')
      && isPreviewAmount(value['apr'], label !== 'APR' && label !== 'Finance')
      && isPreviewUpfrontLabel(value['upfrontLabel'], label);
  }

  return valid;
}

/**
 ** Checks decimal values and fields that must remain empty.
 */
function isPreviewAmount(value: unknown, mustBeNull: boolean): boolean
{
  let valid = value === null;
  if (!mustBeNull) {
    valid = typeof value === 'string' && /^\d+\.\d{2}$/.test(value)
      && Number.isFinite(Number(value));
  }

  return valid;
}

/**
 ** Checks which upfront labels are allowed for each offer type.
 */
function isPreviewUpfrontLabel(value: unknown, label: unknown): boolean
{
  let valid = value === 'Down' || value === 'Due at Signing';
  if (label === 'Finance') {
    valid = value === 'Down';
  }
  if (label === 'APR') {
    valid = value === null;
  }

  return valid;
}

function isRuleData(value: unknown, type: unknown): value is OfferRuleWorkflowRuleData
{
  let valid = false;
  if (isRecord(value) && hasValidRuleKeys(value, type)) {
    valid = isOptionalPositiveSafeInteger(value['year'])
      && isOptionalText(value['make'])
      && isOptionalText(value['model'])
      && isOptionalText(value['trim'])
      && isDecimal(value['maxSalePrice'], maximumCurrency, false)
      && isValidMinimumPrice(value.minSalePrice, value.maxSalePrice)
      && isOptionalNonNegativeSafeInteger(value['vehicleLimit'])
      && isVehicleCondition(value['vehicleCondition'])
      && isRuleStatus(value['status'])
      && isPrivacy(value['privacy']);
    valid = valid && isRuleDiscriminatorValid(value, type);
  }

  return valid;
}

function hasValidRuleKeys(value: Record<string, unknown>, type: unknown): boolean
{
  const keys = [
    'year', 'make', 'model', 'trim', 'minSalePrice', 'maxSalePrice', 'vehicleLimit',
    'vehicleCondition', 'dollarAmountDiscount', 'percentageDiscount', 'discountBasis', 'status', 'privacy'
  ];
  if (type === agedInventoryType) {
    keys.push('maxDiscountAmount', 'ageBuckets');
  }
  const valid = hasOnlyKeys(value, keys);

  return valid;
}

function isRuleDiscriminatorValid(value: Record<string, unknown>, type: unknown): boolean
{
  let valid = false;
  if (type === offerSetType) {
    valid = isPositiveSafeInteger(value['year'])
      && isNonEmptyText(value['make'])
      && isNonEmptyText(value['model'])
      && value['dollarAmountDiscount'] === null
      && value.percentageDiscount == null;
  }
  if (type === dollarDiscountType) {
    valid = value['privacy'] === 'private'
      && isDecimal(value['dollarAmountDiscount'], maximumCurrency, true)
      && value.percentageDiscount == null;
  }
  if (type === percentageDiscountType) {
    valid = value.privacy === 'private'
      && isDecimal(value.percentageDiscount, maximumDiscountPercentage, false)
      && (value.discountBasis === undefined || value.discountBasis === 'selling_price' || value.discountBasis === 'msrp')
      && value.dollarAmountDiscount == null;
  }
  if (type === agedInventoryType) {
    valid = isAgedInventoryRuleData(value);
  }

  return valid;
}

/** Aged GET and status data must contain a complete strategy and no scalar discount values. */
function isAgedInventoryRuleData(value: unknown): value is OfferRuleWorkflowAgedInventoryRuleData
{
  let valid = false;
  if (isRecord(value)) {
    const buckets = value.ageBuckets;
    const cap = value.maxDiscountAmount;
    if (Array.isArray(buckets) && buckets.length > 0 && buckets.every(isAgeBucketData)
      && (cap === null || isExactAgedDecimal(cap, maximumCurrency))) {
      valid = value.privacy === 'private' && value.dollarAmountDiscount === null && value.percentageDiscount === null
        && (value.discountBasis === 'selling_price' || value.discountBasis === 'msrp')
        && hasContinuousAgeRanges(buckets) && hasValidAgeBucketCap(buckets, cap);
    }
  }

  return valid;
}

/** Strict keys prevent bucket identity or a per-bucket discount basis from entering browser state. */
function isAgeBucketData(value: unknown): value is OfferRuleWorkflowAgeBucketData
{
  let valid = false;
  const keys = ['minAgeDays', 'maxAgeDays', 'discountType', 'discountValue'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    const validRange = isAgeDay(value.minAgeDays)
      && (value.maxAgeDays === null || (isAgeDay(value.maxAgeDays) && value.maxAgeDays >= value.minAgeDays));
    let validDiscount = value.discountType === 'dollar' && isExactAgedDecimal(value.discountValue, maximumCurrency);
    if (value.discountType === 'percentage') {
      validDiscount = isExactAgedDecimal(value.discountValue, maximumDiscountPercentage);
    }
    valid = validRange && validDiscount;
  }

  return valid;
}

/** Rejects surrounding whitespace as well as overprecision on the newly introduced decimal fields. */
function isExactAgedDecimal(value: unknown, maximum: string): value is string
{
  const valid = typeof value === 'string' && value === value.trim() && isDecimal(value, maximum, false);

  return valid;
}

/** Compares normalized dollars exactly, preserving null-as-unlimited and zero caps. */
function hasValidAgeBucketCap(buckets: readonly OfferRuleWorkflowAgeBucketData[], cap: unknown): boolean
{
  let valid = cap === null;
  if (typeof cap === 'string') {
    valid = buckets.every(bucket => bucket.discountType !== 'dollar' || compareDecimal(bucket.discountValue, cap) <= 0);
  }

  return valid;
}

function isPersistedOfferSet(value: unknown): boolean
{
  let valid = false;
  const keys = ['sourceOfferSetId', 'generatedOfferSetId', 'visibilityScope', 'offers'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = value['sourceOfferSetId'] === null
      && isPositiveSafeInteger(value['generatedOfferSetId'])
      && isVisibility(value['visibilityScope'])
      && isOffers(value['offers']);
  }

  return valid;
}

function isOffers(value: unknown): value is OfferRuleWorkflowOffersData
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['lease', 'alternateLease', 'finance', 'promotionalApr'])) {
    valid = isLease(value['lease'])
      && isLease(value['alternateLease'])
      && isFinance(value['finance'])
      && isPromotionalApr(value['promotionalApr']);
  }

  return valid;
}

function isLease(value: unknown): value is OfferRuleWorkflowLeaseData
{
  let valid = false;
  const keys = ['mode', 'paymentAmount', 'moneyDown', 'percentageDown', 'term', 'annualMileage', 'additionalText'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isSectionMode(value['mode'])
      && isNullableDecimal(value['paymentAmount'], maximumCurrency)
      && isNullableDecimal(value['moneyDown'], maximumCurrency)
      && isNullableDecimal(value['percentageDown'], maximumPercentage)
      && isOptionalPositiveSafeInteger(value['term'])
      && isOptionalNonNegativeSafeInteger(value['annualMileage'])
      && isOptionalText(value['additionalText'])
      && hasValidCustomLeaseShape(value);
  }

  return valid;
}

function isFinance(value: unknown): value is OfferRuleWorkflowFinanceData
{
  let valid = false;
  const keys = ['mode', 'paymentAmount', 'moneyDown', 'percentageDown', 'apr', 'term', 'additionalText'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isSectionMode(value['mode'])
      && isNullableDecimal(value['paymentAmount'], maximumCurrency)
      && isNullableDecimal(value['moneyDown'], maximumCurrency)
      && isNullableDecimal(value['percentageDown'], maximumPercentage)
      && isNullableDecimal(value['apr'], maximumPercentage)
      && isOptionalPositiveSafeInteger(value['term'])
      && isOptionalText(value['additionalText'])
      && hasValidCustomFinanceShape(value);
  }

  return valid;
}

function isPromotionalApr(value: unknown): value is OfferRuleWorkflowPromotionalAprData
{
  let valid = false;
  const keys = ['mode', 'apr', 'term', 'additionalText'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isSectionMode(value['mode'])
      && isNullableDecimal(value['apr'], maximumPercentage)
      && isOptionalPositiveSafeInteger(value['term'])
      && isOptionalText(value['additionalText']);
    if (value['mode'] === 'custom') {
      valid = valid && value['apr'] !== null && value['term'] !== null;
    }
  }

  return valid;
}

function hasValidCustomLeaseShape(value: Record<string, unknown>): boolean
{
  let valid = true;
  if (value['mode'] === 'custom') {
    const downCount = Number(value['moneyDown'] !== null) + Number(value['percentageDown'] !== null);
    valid = value['paymentAmount'] !== null
      && value['term'] !== null
      && value['annualMileage'] !== null
      && downCount === 1;
  }

  return valid;
}

function hasValidCustomFinanceShape(value: Record<string, unknown>): boolean
{
  let valid = true;
  if (value['mode'] === 'custom') {
    const downCount = Number(value['moneyDown'] !== null) + Number(value['percentageDown'] !== null);
    valid = value['paymentAmount'] !== null
      && value['apr'] !== null
      && value['term'] !== null
      && downCount === 1;
  }

  return valid;
}

function isAcceptance(value: unknown): value is OfferRuleWorkflowAcceptance
{
  let valid = false;
  const keys = ['schemaVersion', 'commandId', 'requestId', 'status', 'phase', 'statusUri'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = value['schemaVersion'] === offerRuleWorkflowSchemaVersion
      && isPositiveSafeInteger(value['commandId'])
      && isRequestId(value['requestId'])
      && value['status'] === 'queued'
      && value['phase'] === 'queued'
      && isNonEmptyString(value['statusUri']);
  }

  return valid;
}

function isCommandStatus(value: unknown): value is OfferRuleWorkflowCommandStatusResponse
{
  let valid = false;
  const keys = [
    'schemaVersion', 'commandId', 'requestId', 'accountId', 'status', 'phase', 'attempt',
    'retryable', 'counts', 'createdAt', 'startedAt', 'completedAt', 'checkpoint', 'created',
    'deleted', 'items', 'authoritativeItemIds', 'error'
  ];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isCommandIdentity(value)
      && isCommandLifecycle(value)
      && isCounts(value['counts'])
      && isOptionalTimestamp(value['createdAt'])
      && isOptionalTimestamp(value['startedAt'])
      && isOptionalTimestamp(value['completedAt'])
      && isOptionalCheckpoint(value['checkpoint'])
      && isCreatedMappings(value['created'])
      && isDeletedMappings(value['deleted'])
      && isAggregateItems(value['items'])
      && isPositiveIdArray(value['authoritativeItemIds'])
      && isOptionalControlledError(value['error']);
  }

  return valid;
}

function isCommandIdentity(value: Record<string, unknown>): boolean
{
  const valid = value['schemaVersion'] === offerRuleWorkflowSchemaVersion
    && isPositiveSafeInteger(value['commandId'])
    && isRequestId(value['requestId'])
    && isPositiveSafeInteger(value['accountId']);

  return valid;
}

function isCommandLifecycle(value: Record<string, unknown>): boolean
{
  let valid = typeof value['retryable'] === 'boolean'
    && (value['attempt'] === null || isPositiveSafeInteger(value['attempt']));
  const status = value['status'];
  const phase = value['phase'];
  if (status === 'queued') {
    valid = valid && phase === 'queued';
  }
  if (status === 'processing') {
    valid = valid && ['persisting', 'syncing_disclosures', 'processing'].includes(String(phase));
  }
  if (status === 'completed') {
    valid = valid && phase === 'completed';
  }
  if (status === 'failed') {
    valid = valid && phase === 'failed';
  }
  valid = valid && ['queued', 'processing', 'completed', 'failed'].includes(String(status));

  return valid;
}

function isCounts(value: unknown): boolean
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['created', 'updated', 'deleted'])) {
    valid = isNonNegativeSafeInteger(value['created'])
      && isNonNegativeSafeInteger(value['updated'])
      && isNonNegativeSafeInteger(value['deleted']);
  }

  return valid;
}

function isOptionalCheckpoint(value: unknown): boolean
{
  let valid = value === null;
  const keys = [
    'failedPhase', 'persistenceCommitted', 'ruleIds', 'generatedOfferSetIds', 'versions',
    'disclosureTargetIds', 'disclosureProcessedIds', 'processingTargetIds', 'processingResultIds'
  ];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isFailedPhase(value['failedPhase'])
      && typeof value['persistenceCommitted'] === 'boolean'
      && isPositiveIdArray(value['ruleIds'])
      && isPositiveIdArray(value['generatedOfferSetIds'])
      && isVersionRecord(value['versions'])
      && isPositiveIdArray(value['disclosureTargetIds'])
      && isPositiveIdArray(value['disclosureProcessedIds'])
      && isPositiveIdArray(value['processingTargetIds'])
      && isPositiveIdArray(value['processingResultIds']);
  }

  return valid;
}

function isCreatedMappings(value: unknown): boolean
{
  let valid = false;
  if (Array.isArray(value)) {
    valid = value.every((mapping) => isCreatedMapping(mapping));
  }

  return valid;
}

function isCreatedMapping(value: unknown): boolean
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['clientKey', 'ruleId', 'generatedOfferSetId', 'version'])) {
    valid = isNonEmptyString(value['clientKey'])
      && isPositiveSafeInteger(value['ruleId'])
      && (value['generatedOfferSetId'] === null || isPositiveSafeInteger(value['generatedOfferSetId']))
      && isNonEmptyString(value['version']);
  }

  return valid;
}

function isDeletedMappings(value: unknown): boolean
{
  let valid = false;
  if (Array.isArray(value)) {
    valid = value.every((mapping) => isDeletedMapping(mapping));
  }

  return valid;
}

function isDeletedMapping(value: unknown): boolean
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['ruleId', 'generatedOfferSetId'])) {
    valid = isPositiveSafeInteger(value['ruleId'])
      && (value['generatedOfferSetId'] === null || isPositiveSafeInteger(value['generatedOfferSetId']));
  }

  return valid;
}

function isOptionalControlledError(value: unknown): boolean
{
  let valid = value === null;
  if (isRecord(value) && hasRequiredAndOptionalKeys(value, ['code', 'message'], ['operation'])) {
    const codes = [
      'invalid_command_parameters', 'not_found', 'validation_failed', 'aggregate_version_conflict',
      'aggregate_integrity_conflict', 'persistence_failed', 'publication_failed',
      'disclosure_sync_failed_manual_intervention', 'processing_failed_manual_intervention',
      'worker_terminated'
    ];
    valid = codes.includes(String(value['code']))
      && isNonEmptyString(value['message'])
      && isOptionalErrorOperation(value['operation']);
  }

  return valid;
}

function isOptionalErrorOperation(value: unknown): boolean
{
  let valid = value === undefined || value === null;
  if (isRecord(value)) {
    const kind = value['kind'];
    valid = hasRequiredAndOptionalKeys(value, ['kind'], ['clientKey', 'ruleId'])
      && ['create', 'update', 'delete'].includes(String(kind))
      && (value['clientKey'] === undefined || isNonEmptyString(value['clientKey']))
      && (value['ruleId'] === undefined || isPositiveSafeInteger(value['ruleId']));
  }

  return valid;
}

function isAggregateItems(value: unknown): boolean
{
  let valid = false;
  if (Array.isArray(value)) {
    valid = value.every((item) => isAggregateItem(item)) && hasUniqueItemIds(value);
  }

  return valid;
}

function hasUniqueItemIds(items: readonly unknown[]): boolean
{
  const ids = items.map((item) => {
    let id: unknown = null;
    if (isRecord(item)) {
      id = item['ruleId'];
    }

    return id;
  });
  const uniqueIds = new Set(ids);

  return uniqueIds.size === ids.length;
}

function isVersionRecord(value: unknown): boolean
{
  let valid = false;
  if (isRecord(value)) {
    valid = Object.entries(value).every(([ruleId, version]) => {
      const numericRuleId = Number(ruleId);

      return isPositiveSafeInteger(numericRuleId) && isNonEmptyString(version);
    });
  }

  return valid;
}

function isPositiveIdArray(value: unknown): boolean
{
  let valid = false;
  if (Array.isArray(value)) {
    const ids = value.filter((id) => isPositiveSafeInteger(id));
    valid = ids.length === value.length && new Set(ids).size === ids.length;
  }

  return valid;
}

function isDecimal(value: unknown, maximum: string, positive: boolean): boolean
{
  let valid = typeof value === 'string'
    && decimalPattern.test(value)
    && compareDecimal(value, maximum) <= 0;
  if (positive) {
    valid = valid && value !== '0.00';
  }

  return valid;
}

/** Accepts historical omission while validating configured inclusive bounds. */
function isValidMinimumPrice(minimum: unknown, maximum: unknown): boolean
{
  let valid = minimum === undefined || minimum === null;
  if (typeof minimum === 'string' && typeof maximum === 'string') {
    valid = isDecimal(minimum, maximumCurrency, false) && compareDecimal(minimum, maximum) <= 0;
  }

  return valid;
}

function isNullableDecimal(value: unknown, maximum: string): boolean
{
  const valid = value === null || isDecimal(value, maximum, false);

  return valid;
}

function isRecord(value: unknown): value is Record<string, unknown>
{
  const valid = typeof value === 'object' && value !== null && !Array.isArray(value);

  return valid;
}

function hasOnlyKeys(value: Record<string, unknown>, allowedKeys: readonly string[]): boolean
{
  const keys = Object.keys(value);
  const valid = keys.length === allowedKeys.length && keys.every((key) => allowedKeys.includes(key));

  return valid;
}

function hasRequiredAndOptionalKeys(
  value: Record<string, unknown>,
  requiredKeys: readonly string[],
  optionalKeys: readonly string[]
): boolean
{
  const keys = Object.keys(value);
  const allowedKeys = [...requiredKeys, ...optionalKeys];
  const valid = requiredKeys.every((key) => keys.includes(key))
    && keys.every((key) => allowedKeys.includes(key));

  return valid;
}

function isPositiveSafeInteger(value: unknown): value is number
{
  const valid = typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

  return valid;
}

function isNonNegativeSafeInteger(value: unknown): value is number
{
  const valid = typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

  return valid;
}

function isOptionalPositiveSafeInteger(value: unknown): boolean
{
  const valid = value === null || isPositiveSafeInteger(value);

  return valid;
}

function isOptionalNonNegativeSafeInteger(value: unknown): boolean
{
  const valid = value === null || isNonNegativeSafeInteger(value);

  return valid;
}

function isNonEmptyString(value: unknown): value is string
{
  const valid = typeof value === 'string' && value.trim() !== '';

  return valid;
}

function isNonEmptyText(value: unknown): boolean
{
  const valid = isNonEmptyString(value) && value.length <= maximumTextLength;

  return valid;
}

function isOptionalText(value: unknown): boolean
{
  const valid = value === null || isNonEmptyText(value);

  return valid;
}

function isRequestId(value: unknown): value is string
{
  const valid = typeof value === 'string' && requestIdPattern.test(value);

  return valid;
}

function isVehicleCondition(value: unknown): boolean
{
  const valid = ['New', 'Used', 'CPO', 'Any'].includes(String(value));

  return valid;
}

function isRuleStatus(value: unknown): boolean
{
  const valid = value === 'Active' || value === 'Inactive';

  return valid;
}

function isPrivacy(value: unknown): boolean
{
  const valid = value === 'public' || value === 'private';

  return valid;
}

function isVisibility(value: unknown): boolean
{
  const valid = value === 'platform_full' || value === 'inventory_only';

  return valid;
}

function isSectionMode(value: unknown): boolean
{
  const valid = value === 'custom' || value === 'default';

  return valid;
}

function isFailedPhase(value: unknown): boolean
{
  const valid = value === null
    || ['queued', 'persisting', 'syncing_disclosures', 'processing'].includes(String(value));

  return valid;
}

function isOptionalTimestamp(value: unknown): boolean
{
  let valid = value === null;
  if (typeof value === 'string' && value.trim() !== '') {
    valid = Number.isFinite(Date.parse(value));
  }

  return valid;
}
